import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';

import { calculateTotalValue } from '../../EstateWaterfallHypotheticalTransfers/estateWaterfallHypotheticalTransfers.utils';
import { useEstateWaterfallHypotheticalCharitableTrustModalContext } from '../EstateWaterfallHypotheticalCharitableTrustModal.context';
import {
  HypotheticalCharitableTrustFormShape,
  HypotheticalCharitableTrustModalKind,
  SourceValues,
} from '../EstateWaterfallHypotheticalCharitableTrustModal.types';
import { useSelectedProposalData } from '../hooks/useSelectedProposalData';

interface FundingWarningProps {
  householdId: string;
  waterfallId: string;
  sourceValues: Record<string, SourceValues>;
}

export function FundingWarning({
  householdId,
  waterfallId,
  sourceValues,
}: FundingWarningProps) {
  const { control } = useFormContext<HypotheticalCharitableTrustFormShape>();
  const { charitableTrustModalKind } =
    useEstateWaterfallHypotheticalCharitableTrustModalContext();

  const [
    proRataPattern,
    sourceId,
    fundingKind,
    proRataAmount,
    proRataPercent,
    selectedAssetClassesAndBusinessIds,
    selectedAssetClassAndBusinessValues,
    assetsPattern,
  ] = useWatch({
    control,
    name: [
      'proRataPattern',
      'sourceId',
      'fundingKind',
      'proRataAmount',
      'proRataPercent',
      'selectedAssetClassesAndBusinessIds',
      'selectedAssetClassAndBusinessValues',
      'assetsPattern',
    ],
  });

  // Get the proposal asset value
  const { proposalAssetValue } = useSelectedProposalData({
    charitableTrustModalKind,
    householdId,
    waterfallId,
  });

  // Calculate totalSourceValue in its own useMemo to avoid changing dependencies
  const totalSourceValue = useMemo(() => {
    return sourceValues[sourceId]?.totalValue ?? new Decimal(0);
  }, [sourceId, sourceValues]);

  // Calculate the current transfer funding amount
  const transferFundingAmount = useMemo(() => {
    if (fundingKind === 'proRata') {
      if (proRataPattern === 'amount' && proRataAmount) {
        return proRataAmount;
      } else if (
        proRataPattern === 'percent' &&
        proRataPercent &&
        totalSourceValue
      ) {
        return totalSourceValue.mul(proRataPercent).div(100);
      }
    } else if (fundingKind === 'assets') {
      const classIdToValue: Record<string, { totalValue: Decimal }> = {};

      Object.entries(sourceValues[sourceId]?.byClassId ?? {}).forEach(
        ([classId, value]) => {
          classIdToValue[classId] = {
            totalValue: value.totalValueForClass,
          };
        }
      );

      return calculateTotalValue({
        selectedAssetIds: selectedAssetClassesAndBusinessIds,
        selectedAssetClassAndBusinessValues,
        assetOptionDetails: classIdToValue,
        pattern: assetsPattern,
      });
    }

    return null;
  }, [
    fundingKind,
    proRataPattern,
    proRataAmount,
    proRataPercent,
    totalSourceValue,
    sourceValues,
    sourceId,
    selectedAssetClassesAndBusinessIds,
    selectedAssetClassAndBusinessValues,
    assetsPattern,
  ]);

  // Don't show warning if we don't have both values
  if (!transferFundingAmount || !proposalAssetValue) {
    return null;
  }

  const isCrt =
    charitableTrustModalKind === HypotheticalCharitableTrustModalKind.CRT;
  const isClt =
    charitableTrustModalKind === HypotheticalCharitableTrustModalKind.CLT;

  // For CRTs: Warning if transfer funding < proposal funding
  const showCrtWarning =
    isCrt && transferFundingAmount.lessThan(proposalAssetValue);

  // For CLTs: Warning if transfer funding > proposal funding
  const showCltWarning =
    isClt && transferFundingAmount.greaterThan(proposalAssetValue);

  if (!showCrtWarning && !showCltWarning) {
    return null;
  }

  return (
    <Callout severity="warning">
      The total funding value does not match the funding value in the selected
      analysis, which may lead to invalid results in the estate waterfall.
      Please ensure the funding amounts match by either updating the funding
      amount above or in the analysis by clicking{' '}
      <strong>‘Edit analysis’</strong> above.
    </Callout>
  );
}
